import { motion } from "framer-motion";
import { AlchamisMark } from "@/components/AlchamisMark";

const PILLARS = [
  {
    title: "Board-Level Advisory",
    description:
      "Partner-led counsel to CEOs, boards, and operating partners — for the decisions that cannot afford to be wrong.",
    metric: "100%",
    metricLabel: "Partner-led engagements",
  },
  {
    title: "Technology Execution",
    description:
      "Strategy without execution is theatre. Our engineers ship the platforms that operationalise our recommendations.",
    metric: "4",
    metricLabel: "In-house enterprise platforms",
  },
  {
    title: "AI Transformation",
    description:
      "AI portfolios calibrated to capital, risk, and capability — productionised with governance, not pilots.",
    metric: "10",
    metricLabel: "AI services productionised",
  },
  {
    title: "Enterprise Software",
    description:
      "PLM, CRM, and category platforms engineered for institutions that consider their software a strategic asset.",
    metric: "$3.4B+",
    metricLabel: "Client revenue under advisement",
  },
  {
    title: "Global Delivery",
    description:
      "Headquartered in New Jersey with a global delivery network. Discretion, multilingual reach, and 24-hour cadence.",
    metric: "42",
    metricLabel: "Industries served globally",
  },
  {
    title: "Industry Expertise",
    description:
      "Engagements led by partners with operating careers inside the industries they advise — not generalists.",
    metric: "15",
    metricLabel: "Integrated practices",
  },
];

export default function WhyLeadersSection() {
  return (
    <section className="section-pad bg-[#FAFAF6] relative overflow-hidden" data-testid="home-why-leaders">
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.10), transparent 55%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 lg:col-span-6">
            <span className="overline">Why Leaders Choose AlchamisGroup</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-[1.05]">
              Six disciplines that compound
              <span className="block italic gold-text">into a single advantage.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 text-[#3F4A6B] text-base leading-relaxed">
            <p>
              Boards and CEOs hire AlchamisGroup when the decision matters and the runway is short. Our
              partnership combines top-tier strategy with engineering depth — the rare consulting house
              that ships both the recommendation and the platform behind it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#001B54]/10">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={`why-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="group border-r border-b border-[#001B54]/10 p-8 lg:p-10 bg-white hover:bg-[#FFFCF4] transition-colors duration-500"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 border border-gold/40 flex items-center justify-center group-hover:border-gold transition-colors bg-[#001B54]">
                  <AlchamisMark size={22} />
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl gold-text">{p.metric}</div>
                  <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#6B7390] mt-1">{p.metricLabel}</div>
                </div>
              </div>
              <h3 className="font-display text-2xl text-[#001B54] mb-3 leading-tight">{p.title}</h3>
              <p className="text-[#3F4A6B] text-sm leading-relaxed">{p.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
